import React, { FC, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import {
  currencySelector,
  findProductSetupById,
  getOrganisationsSettingsUrl,
  hasAllowedGoogleSheetsImports,
  hasFrenchAccountingRules,
  PREDICT_PRODUCT_ID
} from '@futrli/shared-services';
import { LOADER_AREAS, UILoader, UIPromiseTrackerLoader } from '@futrli/ui-components';

import { addNormalisedErrorMessage } from '../../flash-list/flash-list-actions';
import { InitialOnboardingLoader } from '../../../../skeleton/src/onboarding/components/initial-onboarding-nav';
import { IPredictManagementContext, PredictManagementContext } from '../context/predict-management-context';
import { PredictManagement } from '../components/predict-management';
import { useOnboardingUrlInfo } from '../../forecast/onboarding/utils/hooks';
import { usePredictManagementData } from '../hooks/usePredictManagementData';

export const PredictManagementContainer: FC = () => {
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId as string;
  const organisationId = tempOrganisationId as string;
  const { isOnboardingRoute, onboardingForecastId } = useOnboardingUrlInfo();
  const {
    financialConfig,
    isLoading,
    organisation,
    refetchOrganisation,
    shouldRedirect
  } = usePredictManagementData(workspaceId, organisationId, isOnboardingRoute);
  const memoizedAddNormalisedErrorMessage = useMemo(() => addNormalisedErrorMessage(), []);

  if (shouldRedirect) {
    return <Navigate to={getOrganisationsSettingsUrl(workspaceId)} replace />;
  }

  if (isLoading || !organisation) {
    if (isOnboardingRoute || onboardingForecastId) {
      return <InitialOnboardingLoader />;
    }
    return <UILoader isFull={true} />;
  }

  const contextValue: Partial<IPredictManagementContext> = {
    currency: currencySelector(organisation),
    organisation,
    financialConfig,
    refetchOrganisation,
    addErrorMessage: memoizedAddNormalisedErrorMessage,
    isFrenchAccounting: hasFrenchAccountingRules(financialConfig),
    googleSheetImportAllowed: hasAllowedGoogleSheetsImports(financialConfig)
  };

  const productSetup = findProductSetupById(organisation, PREDICT_PRODUCT_ID);

  return (
    <PredictManagementContext.Provider value={contextValue}>
      <PredictManagement productSetup={productSetup} />
      <UIPromiseTrackerLoader area={LOADER_AREAS.predictArea} />
    </PredictManagementContext.Provider>
  );
};
